import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "../../../services/axiosInstance";
import useTheme from "../../../components/web/layoutscommon/DarkModeStorage";
import StoreTermsPopup from "./StoreTermsPopup";
import StoreAuthShell from "./StoreAuthShell";
import {Badge, Btn, ErrorText, Field, Notice} from "./StoreUI";
import {storeLogin, STORE_DASHBOARD_PATH} from "../../../services/storeApi";
import {TbCheck} from "react-icons/tb";

/**
 * 상점사(쇼핑몰) 회원가입
 *  - 경로 : /store/api/join
 *  - 이메일 실시간 중복검사 + 메일인증을 마쳐야 가입할 수 있다.
 *  - 가입 성공 시 자동로그인 후 대시보드로 이동.
 */

/**
 * 약관 동의 체크행
 * ※ 반드시 컴포넌트 '바깥'에 정의해야 한다.
 *   내부에 정의하면 렌더마다 새로운 컴포넌트 타입이 만들어져 React 가 input 을 리마운트하고,
 *   한 글자 입력할 때마다 포커스가 풀리는 버그가 발생한다.
 */
function CheckRow({checked, onToggle, label, onDetail}) {
    return (
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-py-1.5">
            <button type="button" onClick={onToggle} role="checkbox" aria-checked={checked} aria-label={label}
                    className="tw-flex tw-h-[22px] tw-w-[22px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-[7px] tw-border tw-transition-all"
                    style={checked
                        ? {background: 'linear-gradient(135deg,#FFD79A,#FFBE48)', borderColor: '#F0A31F', color: '#4A3410'}
                        : {background: 'var(--st-field)', borderColor: 'var(--st-glass-line)', color: 'transparent'}}>
                <TbCheck className="tw-text-[15px]" strokeWidth={3}/>
            </button>
            <span onClick={onToggle}
                  className="tw-flex-1 tw-cursor-pointer tw-text-[13px] tw-text-[color:var(--st-text-sub)]">{label}</span>
            <button type="button" onClick={onDetail}
                    className="tw-text-[12px] tw-underline tw-underline-offset-2 tw-text-[color:#B8790F] hover:tw-text-[#F0A31F]">
                보기
            </button>
        </div>
    );
}

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
// 영문 + 숫자 + 특수문자 조합 8~20자 (비토리지 회원가입과 동일 규칙)
const PASSWORD_REGEX = /^(?=.*[a-zA-Z])(?=.*[!@#$%^&*()])(?=.*[0-9]).{8,20}$/;

function StoreJoinPg() {

    const [theme] = useTheme();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [name, setName] = useState('');
    const [storeUrl, setStoreUrl] = useState('');
    const [phone, setPhone] = useState('');

    // 메일인증
    const [certCode, setCertCode] = useState('');       // 사용자가 입력한 코드
    const [resCertCode, setResCertCode] = useState(''); // 서버가 발급한 코드
    const [certSent, setCertSent] = useState(false);
    const [certCompleted, setCertCompleted] = useState(false);
    const [certSending, setCertSending] = useState(false);

    const [useTermsAgree, setUseTermsAgree] = useState(false);
    const [infoGatAgree, setInfoGatAgree] = useState(false);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [termsOfUsePopup, setTermsOfUsePopup] = useState(false);
    const [privacyPolicyPopup, setPrivacyPolicyPopup] = useState(false);

    const setError = (key, msg) => setErrors(prev => ({...prev, [key]: msg}));
    const clearError = (key) => setErrors(prev => ({...prev, [key]: ''}));

    /**
     * ══════════════════════════════════════════════════════════════════════════
     *  실시간 검증 (오류문구 '해제' 전용)
     * ──────────────────────────────────────────────────────────────────────────
     *  - 이미 화면에 떠 있는 오류문구만 대상으로 하며, 값이 정상이 되는 즉시 지운다.
     *    ([가입하기] 를 눌러야만 사라지던 문제 해결)
     *  - 아직 입력하지 않은 항목에 미리 오류를 띄우지는 않는다.
     *    (오류 최초 노출은 기존과 동일하게 [가입하기] 클릭 시점 = validate())
     *  - 변경이 없으면 prev 를 그대로 반환해 불필요한 리렌더를 만들지 않는다.
     * ══════════════════════════════════════════════════════════════════════════
     */
    useEffect(() => {
        setErrors(prev => {
            const next = {...prev};
            let changed = false;

            /** 이미 오류가 떠 있고 지금은 정상이면 문구를 지운다 */
            const resolve = (key, isValid) => {
                if (next[key] && isValid) {
                    next[key] = '';
                    changed = true;
                }
            };

            resolve('password', PASSWORD_REGEX.test(password));
            resolve('passwordConfirm', !!passwordConfirm && password === passwordConfirm);
            resolve('companyName', !!companyName.trim());
            resolve('storeUrl', !!storeUrl.trim());
            resolve('name', !!name.trim());
            resolve('phone', !!phone && phone.length >= 9);
            resolve('cert', certCompleted);

            // 약관동의는 두 항목을 모두 체크해야 해제되며,
            // 하나만 체크한 상태에서는 남은 항목 문구로 정확히 바꿔준다.
            if (next.agree) {
                if (useTermsAgree && infoGatAgree) {
                    next.agree = '';
                    changed = true;
                } else {
                    const msg = !useTermsAgree ? '서비스 이용약관에 동의해주세요' : '개인정보 수집에 동의해주세요';
                    if (next.agree !== msg) {
                        next.agree = msg;
                        changed = true;
                    }
                }
            }

            return changed ? next : prev;
        });
    }, [password, passwordConfirm, companyName, storeUrl, name, phone,
        certCompleted, useTermsAgree, infoGatAgree]);

    /** 이메일 실시간 중복검사 */
    const handleEmailChange = async (value) => {
        setEmail(value);
        setCertSent(false);
        setCertCompleted(false);
        setResCertCode('');

        if (!value) {
            clearError('email');
            return;
        }
        if (!EMAIL_REGEX.test(value)) {
            setError('email', '올바른 이메일 형식이 아닙니다');
            return;
        }
        try {
            const res = await axios.get('/store/existsEmail', {params: {email: value}});
            if (res.data === true) setError('email', '이미 가입된 이메일입니다');
            else clearError('email');
        } catch (e) {
            clearError('email');
        }
    };

    /** 인증코드 발송 */
    const sendCertCode = async () => {
        if (certSending) return;
        if (!EMAIL_REGEX.test(email)) {
            setError('email', '올바른 이메일 형식이 아닙니다');
            return;
        }
        if (errors.email) return;

        setCertSending(true);
        try {
            const res = await axios.get('/store/generateCertcode', {params: {email}});
            setResCertCode(String(res.data || ''));
            setCertSent(true);
            clearError('cert');
        } catch (e) {
            setError('cert', '인증코드 발송에 실패했습니다');
        } finally {
            setCertSending(false);
        }
    };

    /** 인증코드 확인 */
    const confirmCertCode = () => {
        if (!resCertCode) return;
        if (certCode.trim() === resCertCode) {
            setCertCompleted(true);
            clearError('cert');
        } else {
            setCertCompleted(false);
            setError('cert', '인증코드가 일치하지 않습니다');
        }
    };

    const handlePhoneChange = (value) => {
        // '-' 없이 숫자만 저장 (스펙)
        setPhone(value.replace(/[^0-9]/g, '').slice(0, 12));
    };

    const validate = () => {
        const next = {};
        if (!EMAIL_REGEX.test(email)) next.email = '올바른 이메일 형식이 아닙니다';
        if (!certCompleted) next.cert = '메일인증을 완료해주세요';
        if (!PASSWORD_REGEX.test(password)) next.password = '영문·숫자·특수문자를 포함한 8~20자로 입력해주세요';
        if (password !== passwordConfirm) next.passwordConfirm = '비밀번호가 일치하지 않습니다';
        if (!companyName.trim()) next.companyName = '상호명을 입력해주세요';
        if (!storeUrl.trim()) next.storeUrl = '자사 URL을 입력해주세요';
        if (!name.trim()) next.name = '담당자 성함을 입력해주세요';
        if (!phone || phone.length < 9) next.phone = '담당자 연락처를 정확히 입력해주세요';
        if (!useTermsAgree) next.agree = '서비스 이용약관에 동의해주세요';
        else if (!infoGatAgree) next.agree = '개인정보 수집에 동의해주세요';

        setErrors(next);
        return Object.values(next).every(v => !v);
    };

    const handleJoin = async () => {
        if (loading) return;
        if (!validate()) return;

        setLoading(true);
        try {
            await axios.post('/store/join', {
                email: email.trim(),
                password,
                companyName: companyName.trim(),
                storeUrl: storeUrl.trim(),
                name: name.trim(),
                phone,
                useTermsAgree,
                infoGatAgree,
            });
            // 가입 직후 자동로그인
            await storeLogin(email.trim(), password);
            navigate(STORE_DASHBOARD_PATH, {replace: true});
        } catch (e) {
            setError('submit', e?.response?.data?.message || '가입 처리 중 오류가 발생했습니다');
        } finally {
            setLoading(false);
        }
    };

    return (
        <StoreAuthShell theme={theme}
                        title="비토리지와 연결하기"
                        desc="가입 후 비토리지가 활성화하면 API 키를 바로 사용하실 수 있습니다"
                        maxWidth={520}
                        onTerms={() => setTermsOfUsePopup(true)}
                        onPrivacy={() => setPrivacyPolicyPopup(true)}>

            {/*─── 계정 ───*/}
            <p className="st-eyebrow tw-mb-3">계정</p>

            <Field label="이메일" error={errors.email} required>
                <div className="tw-flex tw-gap-2">
                    <input className="st-input" type="text" value={email} maxLength={50} placeholder="example@company.com"
                           onChange={(e) => handleEmailChange(e.target.value)}/>
                    {/*
                      메일발송은 수 초가 걸릴 수 있으므로 진행중에는 버튼 문구를 로딩모션으로 대체한다.
                      (disabled + sendCertCode 내부 가드로 중복클릭도 함께 차단)
                      너비를 고정해 문구↔스피너 전환 시 레이아웃이 흔들리지 않게 한다.
                    */}
                    <Btn tone={certCompleted ? 'glass' : 'dawn'} size="md"
                         className="tw-w-[96px] tw-flex-shrink-0"
                         onClick={sendCertCode} busy={certSending}
                         disabled={certSending || certCompleted || !!errors.email || !email}>
                        {certCompleted ? '인증완료' : certSent ? '재발송' : '인증요청'}
                    </Btn>
                </div>
            </Field>

            {certSent && !certCompleted ? (
                <Field label="인증코드" error={errors.cert}>
                    <div className="tw-flex tw-gap-2">
                        <input className="st-input st-mono tw-tracking-[.4em]" type="text" value={certCode}
                               maxLength={6} inputMode="numeric" placeholder="6자리"
                               onChange={(e) => setCertCode(e.target.value.replace(/[^0-9]/g, ''))}/>
                        <Btn tone="glass" size="md" className="tw-w-[96px] tw-flex-shrink-0" onClick={confirmCertCode}>
                            확인
                        </Btn>
                    </div>
                </Field>
            ) : null}

            {certCompleted ? (
                <div className="tw-mb-4 tw--mt-1">
                    <Badge tone="sage" dot>메일인증 완료</Badge>
                </div>
            ) : null}

            <Field label="비밀번호" error={errors.password} required>
                <input className="st-input" type="password" value={password} maxLength={20}
                       placeholder="영문·숫자·특수문자 포함 8~20자"
                       onChange={(e) => setPassword(e.target.value)}/>
            </Field>

            <Field label="비밀번호 확인" error={errors.passwordConfirm} required>
                <input className="st-input" type="password" value={passwordConfirm} maxLength={20}
                       placeholder="비밀번호를 한 번 더 입력해주세요"
                       onChange={(e) => setPasswordConfirm(e.target.value)}/>
            </Field>

            {/*─── 상점 ───*/}
            <p className="st-eyebrow tw-mb-3 tw-mt-7">상점</p>

            <Field label="상호명" error={errors.companyName} required>
                <input className="st-input" type="text" value={companyName} maxLength={50}
                       placeholder="사업자 상호명을 입력해주세요"
                       onChange={(e) => setCompanyName(e.target.value)}/>
            </Field>

            <Field label="자사 URL" error={errors.storeUrl} required
                   hint="앱 서비스라면 앱 이름을 적어주세요">
                <input className="st-input" type="text" value={storeUrl} maxLength={500}
                       placeholder="https://mystore.com"
                       onChange={(e) => setStoreUrl(e.target.value)}/>
            </Field>

            <Field label="담당자 성함" error={errors.name} required>
                <input className="st-input" type="text" value={name} maxLength={30}
                       placeholder="담당자 성함을 입력해주세요"
                       onChange={(e) => setName(e.target.value)}/>
            </Field>

            <Field label="담당자 연락처" error={errors.phone} required>
                <input className="st-input st-num" type="text" value={phone} maxLength={12} inputMode="numeric"
                       placeholder="'-' 없이 숫자만 입력해주세요"
                       onChange={(e) => handlePhoneChange(e.target.value)}/>
            </Field>

            {/*─── 동의 ───*/}
            <div className="tw-mt-7 tw-rounded-[16px] tw-border tw-px-4 tw-py-3"
                 style={{background: 'rgba(203,182,255,.10)', borderColor: 'var(--st-glass-line)'}}>
                <CheckRow checked={useTermsAgree} onToggle={() => setUseTermsAgree(v => !v)}
                          label="[필수] 서비스 이용약관 동의" onDetail={() => setTermsOfUsePopup(true)}/>
                <CheckRow checked={infoGatAgree} onToggle={() => setInfoGatAgree(v => !v)}
                          label="[필수] 개인정보 수집·이용 동의" onDetail={() => setPrivacyPolicyPopup(true)}/>
            </div>

            {errors.agree ? <ErrorText>{errors.agree}</ErrorText> : null}
            {errors.submit ? <div className="tw-mt-3"><Notice tone="warn">{errors.submit}</Notice></div> : null}

            <Btn tone="dawn" size="lg" className="tw-mt-6 tw-w-full" onClick={handleJoin}
                 disabled={loading} busy={loading}>
                가입하기
            </Btn>

            <div className="tw-mt-5 tw-text-center tw-text-[13px]">
                <button type="button" className="st-link" onClick={() => navigate('/store/api/login')}>
                    이미 계정이 있으신가요? 로그인
                </button>
            </div>

            {termsOfUsePopup ?
                <StoreTermsPopup termsOfUsePopup={termsOfUsePopup} closeTermsPopup={() => setTermsOfUsePopup(false)}/>
                : null}
            {privacyPolicyPopup ?
                <StoreTermsPopup privacyPolicyPopup={privacyPolicyPopup} closeTermsPopup={() => setPrivacyPolicyPopup(false)}/>
                : null}
        </StoreAuthShell>
    );
}

export default StoreJoinPg;
